import { Container, Graphics, Text } from "pixi.js";
import { COLORS, TEXT_STYLE } from "./theme.js";

export type ButtonView = {
  container: Container;
  background: Graphics;
  label: Text;
  setEnabled: (enabled: boolean) => void;
  setSelected: (selected: boolean) => void;
};

type ButtonOptions = {
  width: number;
  height: number;
  label: string;
  onPress: () => void;
  fontSize?: number;
  fill?: number;
};

export function createButton(options: ButtonOptions): ButtonView {
  const container = new Container();
  const background = new Graphics();
  const label = new Text({
    text: options.label,
    style: {
      ...TEXT_STYLE,
      fontSize: options.fontSize || 22,
      fontWeight: "700",
      align: "center",
    },
  });
  label.anchor.set(0.5);
  label.position.set(options.width / 2, options.height / 2);
  container.addChild(background, label);
  container.eventMode = "static";
  container.cursor = "pointer";

  let enabled = true;
  let selected = false;
  let hovered = false;
  let pressed = false;
  let currentScale = 1;
  let targetScale = 1;
  let currentLift = 0;
  let targetLift = 0;
  let animationFrame = 0;
  const animateTransform = function () {
    animationFrame = 0;
    if (container.destroyed) {
      return;
    }
    currentScale += (targetScale - currentScale) * 0.28;
    currentLift += (targetLift - currentLift) * 0.28;
    container.scale.set(currentScale);
    container.pivot.y = currentLift;
    if (Math.abs(targetScale - currentScale) > 0.001 || Math.abs(targetLift - currentLift) > 0.05) {
      animationFrame = window.requestAnimationFrame(animateTransform);
    } else {
      currentScale = targetScale;
      currentLift = targetLift;
      container.scale.set(currentScale);
      container.pivot.y = currentLift;
    }
  };
  const setInteractionTarget = function () {
    targetScale = pressed ? 0.965 : hovered && enabled ? 1.035 : 1;
    targetLift = pressed ? 1 : hovered && enabled ? 3 : 0;
    if (!animationFrame) {
      animationFrame = window.requestAnimationFrame(animateTransform);
    }
  };
  const redraw = function () {
    const fill = selected ? COLORS.goldDark : (options.fill || COLORS.ownDark);
    background.clear()
      .roundRect(0, 0, options.width, options.height, 12)
      .fill({ color: fill, alpha: enabled ? 0.96 : 0.48 })
      .stroke({ color: selected ? COLORS.gold : COLORS.parchmentDark, width: selected ? 3 : 2, alpha: 0.9 });
    label.alpha = enabled ? 1 : 0.55;
  };
  redraw();

  container.on("pointertap", function () {
    if (enabled) {
      options.onPress();
    }
  });
  container.on("pointerdown", function () {
    if (enabled) {
      pressed = true;
      setInteractionTarget();
    }
  });
  container.on("pointerup", function () {
    pressed = false;
    setInteractionTarget();
  });
  container.on("pointerupoutside", function () {
    pressed = false;
    setInteractionTarget();
  });
  container.on("pointerover", function () {
    hovered = true;
    setInteractionTarget();
  });
  container.on("pointerout", function () {
    hovered = false;
    pressed = false;
    setInteractionTarget();
  });

  return {
    container,
    background,
    label,
    setEnabled: function (nextEnabled: boolean) {
      enabled = nextEnabled;
      if (!enabled) {
        pressed = false;
      }
      container.eventMode = enabled ? "static" : "none";
      container.cursor = enabled ? "pointer" : "default";
      setInteractionTarget();
      redraw();
    },
    setSelected: function (nextSelected: boolean) {
      selected = nextSelected;
      redraw();
    },
  };
}

export function createPanel(width: number, height: number, alpha = 0.94): Graphics {
  return new Graphics()
    .roundRect(0, 0, width, height, 18)
    .fill({ color: COLORS.panel, alpha })
    .stroke({ color: COLORS.parchmentDark, width: 2, alpha: 0.85 });
}
